<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useInsightDetail } from '../../composables/useInsightDetail'
import type { InsightStatus } from '../../models/insight.types'
import {
  hasInsightGuidance,
  insightCsvName,
  insightExportName,
  resolveInsightGuidance,
} from '../../service/insightGuidance'
import {
  confidenceLabel,
  outcomePresentation,
  presentInsightTitle,
} from '../../service/insightPresentation'
import { runNonBlockingStatusWrite } from '../../service/nonBlockingStatusWrite'

import InsightElementList from './InsightElementList.vue'
import type { InsightCsvRequest } from './InsightElementList.vue'
import InsightGuidanceCard from './InsightGuidanceCard.vue'
import InsightImpactCard from './InsightImpactCard.vue'
import InsightOutcomeBadge from './InsightOutcomeBadge.vue'
import InsightSeverityBadge from './InsightSeverityBadge.vue'

import {
  InsightActionConfirm,
  InsightActionFooter,
  InsightActionHistory,
  InsightActionPanel,
  InsightActionProgress,
  InsightActionResult,
  InsightActionSelect,
  InsightActionStepFooter,
  useInsightActionSurface,
  useInsightActionStore,
} from '@/features/insight-actions'
import {
  FEATURE_CHAT_KEYS,
  FeatureChatLauncher,
  FeatureChatPanel,
  featureChatTargetRef,
  FEATURE_CHAT_PANEL_WIDTH,
} from '@/features/orpheus'
import { useI18n } from '@/shared/i18n'
import { exportCsv, exportPdf } from '@/shared/lib/export'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Drawer } from '@/shared/ui/drawer'
import { RichTextViewer } from '@/shared/ui/editor'
import { Icon } from '@/shared/ui/icon'
import { Skeleton, SkeletonText } from '@/shared/ui/skeleton'

const props = defineProps<{ insightId: number | null }>()
const open = defineModel<boolean>('open', { default: false })
const emit = defineEmits<{ 'status-changed': [status: InsightStatus] }>()
const router = useRouter()
const { t } = useI18n()
const format = useFormat()
const activeInsightId = computed(() => (open.value ? props.insightId : null))
const detail = useInsightDetail(activeInsightId)
let trackedInsightId: number | null = null

const insight = computed(() => detail.insight.value)

const insightActions = useInsightActionSurface(() => (open.value ? props.insightId : null))
const insightActionStore = useInsightActionStore()
const outcome = computed(() => outcomePresentation(insight.value?.outcome_category))
const title = computed(
  () =>
    [insight.value?.title, insight.value?.label, insight.value?.config_enum]
      .map(presentInsightTitle)
      .find(Boolean) ?? t('insights.detail.untitled'),
)
const hasImpact = computed(() => {
  const value = Number(insight.value?.impact_score)
  return Number.isFinite(value) && value !== 0
})
const guidance = computed(() => resolveInsightGuidance(insight.value))
const showGuidance = computed(() => hasInsightGuidance(guidance.value))

// Ask Orpheus about THIS insight (ORP-20465). `insight_id` is the ref the backend's insights
// integration normalizes; the panel opens closed and asks its opening question on first open.
const featureChatOpen = ref(false)
// v3 offset the host sidebar by the panel's live width so the two sat side by side; the panel
// reports its own drags through `@resize` (`FeatureOrpheusPanel`'s `@width-change`).
const featureChatWidth = ref(FEATURE_CHAT_PANEL_WIDTH)
const featureChatQuestion = computed(() => t('orpheus.featureChat.question.insights'))
const featureChatTarget = computed(() => featureChatTargetRef('insight_id', insight.value?.id))

// TWO capture targets, not one. The `Drawer` puts the badges, title and date in its `#header`
// slot and the content in its default slot, so no single element contains the insight and nothing
// else — their common ancestor is the drawer panel, which also holds the overlay and the scroll
// viewport. v3 hit the same wall and stitched the two subtrees into one synthetic export root
// (`useInsightExport.js:45-74`); the shared adapter now takes the pair and does that stitching.
const exportHeader = ref<HTMLElement | null>(null)
const exportSurface = ref<HTMLElement | null>(null)
const exporting = ref(false)
const exportError = ref(false)
const csvError = ref(false)

async function downloadPdf(): Promise<void> {
  if (!exportHeader.value || !exportSurface.value || exporting.value) return
  exporting.value = true
  exportError.value = false
  try {
    await exportPdf([exportHeader.value, exportSurface.value], insightExportName(insight.value), {
      telemetry: { fileFormat: 'pdf', featureName: 'insight-detail' },
    })
  } catch {
    exportError.value = true
  } finally {
    exporting.value = false
  }
}

async function downloadCsv(request: InsightCsvRequest): Promise<void> {
  csvError.value = false
  try {
    await exportCsv(
      { name: request.name, columns: request.columns, data: request.rows },
      insightCsvName(insight.value, new Date()),
    )
  } catch {
    csvError.value = true
  }
}

const hasNegativeKeywordAction = computed(() =>
  (insight.value?.elements ?? []).some((element) =>
    (element.column_definitions ?? []).some((column) => column.key === 'matched_keywords_chips'),
  ),
)

function markClicked(): void {
  runNonBlockingStatusWrite(
    () => detail.updateStatus('clicked'),
    () => emit('status-changed', 'clicked'),
    'clicked',
  )
}

watch(
  [open, () => props.insightId] as const,
  ([isOpen, insightId]) => {
    if (!isOpen) {
      trackedInsightId = null
      featureChatOpen.value = false
      return
    }
    if (insightId === null || trackedInsightId === insightId) return
    trackedInsightId = insightId
    // A new record is a new conversation: the panel collapses rather than carrying the previous
    // insight's transcript into this one.
    featureChatOpen.value = false
    markClicked()
  },
  { immediate: true },
)
</script>

<template>
  <Drawer
    v-model="open"
    :offset="featureChatOpen ? featureChatWidth : 0"
    size="xl"
    resizable
    :width-override="insightActions.panelWidthOverride.value"
    :close-label="t('common.close')"
    min-width="720px"
    class="insight-detail [&_.ui-drawer\_\_title]:grow [&_.ui-drawer\_\_header]:items-start"
  >
    <template #header>
      <div
        ref="exportHeader"
        data-pdf-section
        class="insight-detail__header grid gap-md min-w-0 w-full gap-xs"
      >
        <div v-if="insight" class="insight-detail__badges flex flex-wrap items-center gap-xs">
          <InsightOutcomeBadge :value="insight.outcome_category" strong />
          <InsightSeverityBadge :value="insight.severity" strong />
          <Badge v-if="insight.confidence" class="insight-detail__confidence" tone="info">{{
            confidenceLabel(insight.confidence, t)
          }}</Badge>
          <!-- v3 kept this as an icon-only control sharing the badge row's trailing edge with the
               close button (`InsightDetailModal.vue:12` + `InsightExportActions.vue:2-11`), so
               `ms-auto` parks it there and the drawer's close — a flex SIBLING of this slot, not an
               absolute overlay — lands immediately after it. The two `:deep()` rules below are what
               make that work without changing the shared component: the drawer's title cell is
               content-sized, so `ms-auto` would otherwise stop at the end of the heading text, and
               its header centres the close against the whole block rather than this row. They are
               utilities rather than scoped `:deep()` rules — the rest of this file's descendant
               copy lives in `<style scoped>` — because a scoped rule provably cannot reach here:
               `class` lands on the drawer's dialog through its manual `v-bind="$attrs"`, not
               through root-node inheritance, so that element carries no `data-v` scope id and
               `.insight-detail :deep(…)` matches nothing.
               `file-pdf` at `regular` weight is v3's own glyph (`InsightExportActions.vue:9`).
               `!text-muted` because the `ghost` variant paints `text-fg`, which read darker than
               the close button beside it — the two share one grey. -->
          <Button
            data-pdf-ignore
            class="insight-detail__export ms-auto !text-muted"
            variant="ghost"
            size="sm"
            square
            :loading="exporting"
            :aria-label="t('insights.detail.exportPdf')"
            :title="exporting ? t('insights.detail.exporting') : t('insights.detail.exportPdf')"
            @click="downloadPdf"
          >
            <Icon v-if="!exporting" name="file-pdf" weight="regular" :size="17" />
          </Button>
        </div>
        <h2>{{ insight ? title : t('insights.detail.title') }}</h2>
        <span v-if="insight?.created_at" class="insight-detail__date text-muted text-[0.75rem]">
          {{ format.formatDate(insight.created_at) }}
        </span>
        <Alert
          v-if="exportError"
          data-pdf-ignore
          tone="danger"
          :title="t('insights.detail.exportFailed')"
        />
        <Alert
          v-if="csvError"
          data-pdf-ignore
          tone="danger"
          :title="t('insights.detail.exportCsvFailed')"
        />
      </div>
    </template>

    <div
      ref="exportSurface"
      class="insight-detail__body grid gap-md min-w-0"
      data-testid="insight-detail-modal"
    >
      <InsightActionPanel>
        <template #read>
          <div
            v-if="detail.state.value === 'loading'"
            class="insight-detail__loading grid gap-md min-w-0"
          >
            <SkeletonText :lines="3" />
            <Skeleton height="7rem" />
            <Skeleton height="14rem" />
          </div>

          <Alert
            v-else-if="detail.state.value === 'error'"
            tone="danger"
            :title="t('insights.error.load')"
          >
            <Button variant="outline" size="xs" @click="detail.reload">
              {{ t('common.retry') }}
            </Button>
          </Alert>

          <p v-else-if="!insight" class="insight-detail__empty m-0 p-xl text-muted text-center">
            {{ t('insights.detail.unavailable') }}
          </p>

          <template v-else>
            <!-- `data-pdf-ignore` keeps the chat out of the insight's PDF: this whole body IS the
                 export surface, and a transcript is not part of the insight. -->
            <FeatureChatLauncher
              v-if="featureChatTarget"
              data-pdf-ignore
              :question="featureChatQuestion"
              @ask="featureChatOpen = true"
            />
            <FeatureChatPanel
              v-if="featureChatOpen && featureChatTarget"
              data-pdf-ignore
              :feature-key="FEATURE_CHAT_KEYS.INSIGHTS"
              :target-ref="featureChatTarget"
              :focus-key="insight.id"
              :question="featureChatQuestion"
              :context-label="title"
              @close="featureChatOpen = false"
              @resize="featureChatWidth = $event"
            />

            <section
              data-pdf-section
              class="insight-detail__summary flex items-start justify-between gap-md max-md:items-stretch max-md:flex-col"
            >
              <div class="insight-detail__narrative grid gap-md min-w-0 flex-1 gap-xs">
                <small>{{ t('insights.detail.insight') }}</small>
                <RichTextViewer :content="insight.content || t('insights.detail.noContent')" />
              </div>
              <Button
                v-if="hasNegativeKeywordAction"
                variant="soft"
                size="sm"
                @click="router.push('/workspace-settings/workspace/negative-keywords')"
              >
                {{ t('insights.detail.editNegativeKeywords') }}
              </Button>
            </section>

            <InsightImpactCard
              v-if="hasImpact"
              data-pdf-section
              :outcome="outcome"
              :score="insight.impact_score"
              :impact-format="insight.impact_format"
            />

            <InsightElementList
              :elements="detail.elements.value"
              :tabs="insight.tabs ?? []"
              :config-columns="insight.config?.column_definitions ?? []"
              :empty-label="t('insights.elements.empty')"
              @export-csv="downloadCsv"
            />

            <!-- Sparse on purpose: ~9% of production insights carry guidance, so the whole block is
                 absent rather than showing empty headings. -->
            <InsightGuidanceCard v-if="showGuidance" :guidance="guidance" />
          </template>
        </template>

        <template #select><InsightActionSelect /></template>
        <template #confirm><InsightActionConfirm /></template>
        <template #progress><InsightActionProgress /></template>
        <template #result><InsightActionResult /></template>
        <template #history><InsightActionHistory /></template>
      </InsightActionPanel>
    </div>

    <template v-if="insightActions.footerVisible.value" #footer>
      <InsightActionStepFooter v-if="insightActionStore.mode !== 'read'" />
      <InsightActionFooter v-else :gate="insightActions.gate.value" />
    </template>
  </Drawer>
</template>

<style scoped>
.insight-detail .insight-detail__confidence {
  color: var(--ui-color-insight-confidence);
  background-color: var(--ui-color-insight-confidence-soft);
}
.insight-detail .insight-detail__header h2 {
  margin: 0;
  color: var(--ui-color-fg);
  font-size: 1.25rem;
  overflow-wrap: anywhere;
}
.insight-detail .insight-detail__narrative small {
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}
</style>
